import * as React from "react";
import {
  Switch,
  Route,
  useRouteMatch,
  useLocation,
  Redirect,
  NavLink,
} from "react-router-dom";

import { Error404 } from "@skv/core";
import { Helmet } from "react-helmet";

import { Dashboard } from "./Dashboard";
import { Suggestions } from "./Suggestions";
import { Subscriptions } from "./Subscriptions";
import { Applications } from "./Applications";
import { Experiences } from "./Experiences";

import { useUserState } from "~/state";
import { AddExperience } from "./AddExperience";
import { Favourites } from "./Favourites";

interface LayoutProps {
  header?: React.ReactNode;
  main: React.ReactNode;
  title: string;
}

export const Layout: React.FC<LayoutProps> = ({ header, main, title }) => {
  const location = useLocation();
  const isFavouriteTab = useRouteMatch("/my-activity/favourites");
  const isEmailAlertsTab = useRouteMatch("/my-activity/subscriptions");
  const isExperiencesTab = isCurrentPathMatching(location.pathname, [
    "/my-activity/experiences",
    "/my-activity/experience",
  ]);
  const isApplicationsTab = useRouteMatch("/my-activity/applications");

  function isCurrentPathMatching(currentPath, paths) {
    return paths.some((path) => currentPath.startsWith(path));
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <section className="pb-2">{header}</section>
      <div className="row">
        <aside className="col-lg-3 mb-4 mb-lg-5">
          <nav className="nsw-side-nav" aria-labelledby="my-activity-side-nav">
            <h2
              className="nsw-side-nav__header pb-4 mb-0"
              id="my-activity-side-nav"
            >
              My Activity
            </h2>
            <ul>
              <li className={isFavouriteTab ? "active" : ""}>
                <NavLink
                  to="/my-activity/favourites"
                  activeClassName="current"
                  aria-current="page"
                >
                  Favourites
                </NavLink>
              </li>
              <li className={isEmailAlertsTab ? "active" : ""}>
                <NavLink
                  to="/my-activity/subscriptions"
                  activeClassName="current"
                  aria-current="page"
                >
                  Email alerts
                </NavLink>
              </li>
              <li className={isExperiencesTab ? "active" : ""}>
                <NavLink
                  to="/my-activity/experiences"
                  activeClassName="current"
                  aria-current="page"
                >
                  Experiences
                </NavLink>
              </li>
              <li className={isApplicationsTab ? "active" : ""}>
                <NavLink
                  to="/my-activity/applications"
                  activeClassName="current"
                  aria-current="page"
                >
                  Applications
                </NavLink>
              </li>
            </ul>
          </nav>
        </aside>

        <section className="col-lg-9 pl-lg-5">{main}</section>
      </div>
    </>
  );
};

export default function MyActivity() {
  const match = useRouteMatch();

  const user = useUserState();

  if (!user.isAuthenticated) {
    return <Redirect to="/signin" />;
  }

  const LayoutHeader = ({ title, description }) => (
    <header className="my-5">
      <h1 className="display-1 font-weight-bold pb-2">{title}</h1>
      <p className="lead">{description}</p>
    </header>
  );

  return (
    <Switch>
      <Route exact path={match.path}>
        <Dashboard />
      </Route>
      {/* edit experience */}
      {/* <Route path={`${match.path}/experience/:id`}>
        <Layout main={<AddExperience />} />
      </Route> */}
      {/* add a new experience */}
      <Route path={`${match.path}/experience/:id?`}>
        <Layout
          title="Add volunteer experience"
          header={
            // empty span is to keep the header height consistent
            <span className="d-block mb-4" />
          }
          main={<AddExperience />}
        />
      </Route>

      {/* list of experiences */}
      <Route path={`${match.path}/experiences`}>
        <Layout
          title="Your Experiences"
          header={
            <LayoutHeader
              title="Experiences"
              description="This is a list of your volunteering experience. You can include any volunteer experience, not just opportunities from this site."
            />
          }
          main={<Experiences />}
        />
      </Route>
      <Route path={`${match.path}/subscriptions`}>
        <Layout
          title="Your Subscriptions"
          header={
            <LayoutHeader
              title="Email alerts"
              description="Email alerts make sure you never miss out on a new opportunity. To save an email alert, search for opportunities you are interested in, then save the email alert at the top or bottom of the search results."
            />
          }
          main={<Subscriptions />}
        />
      </Route>
      <Route path={`${match.path}/favourites`}>
        <Layout
          title="Your Favourites"
          header={
            <LayoutHeader
              title="Favourites"
              description="A list of your favourite opportunities and organisations."
            />
          }
          main={<Favourites />}
        />
      </Route>
      <Route path={`${match.path}/suggestions`}>
        <Layout
          title="Your Suggestions"
          header={
            <LayoutHeader
              title="Suggestions"
              description="This is a list of personalised recommendations based on your email alerts."
            />
          }
          main={<Suggestions />}
        />
      </Route>
      <Route path={`${match.path}/applications`}>
        <Layout
          title="Your Applications"
          header={
            <LayoutHeader
              title="Applications"
              description="This is a list of opportunities you have previously applied for on this site, and on other partner sites."
            />
          }
          main={<Applications />}
        />
      </Route>

      <Route>
        <Error404 />
      </Route>
    </Switch>
  );
}
